import { openImpersonationSession } from 'utils/open-impersonation-session';
import React, { FC, Fragment, useCallback, useState } from 'react';
import { useQuery, useLazyQuery, NetworkStatus } from '@apollo/client';
import { getDentistsForUserListTemplate, getImpersonateUserTokenTemplate } from 'apollo/templates/user/query.graphql';
import debounce from 'lodash.debounce';
import { Box } from '@mui/material';
import AddDentistDialog from 'components/users/dentists/add-dentist-dialog/add-dentist-dialog.component';
import DentistList from 'components/users/dentists/dentist-list/dentist-list.component';
import AdminDentistPageActions from 'components/users/dentists/dentist-page-actions/admin-dentist-page-actions.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import { User, UserListFilter } from 'models/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import { useTranslation } from 'react-i18next';
import ImpersonateUserOrganizationsDialog from 'components/shared/impersonate-user-organizations-dialog/impersonate-user-organizations-dialog.component';

const AdminDentists: FC = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [filter, setFilter] = useState<UserListFilter>({
        freeText: '',
        active: true,
    });
    const [clinicId] = useState<string | null>(null);
    const [lastOffset, setLastOffset] = useState(0);
    const [organizationsDialogOpen, setOrganizationsDialogOpen] = useState(false);
    const [user, setUser] = useState<User>({} as User);
    const [wlLabId, setWlLabId] = useState<string | undefined>();
    const { t: translate } = useTranslation(['users']);
    const { showBoundary } = useDenthubErrorBoundary();

    const {
        data: { dentists } = {},
        loading: dentistsLoading,
        fetchMore,
        refetch,
        networkStatus,
    } = useQuery(getDentistsForUserListTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            clinicId: clinicId,
            filters: { freeText: '', active: true } as UserListFilter,
            limit: 25,
            offset: 0,
        },
        onCompleted: ({ dentists }) => {
            setLastOffset(dentists.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [getImpersonateUserToken] = useLazyQuery(getImpersonateUserTokenTemplate, {
        fetchPolicy: 'network-only',
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ impersonateUser }) => {
            handleClose();
            openImpersonationSession(impersonateUser.token, wlLabId);
        },
    });

    const handleFetchMore = () => {
        fetchMore({
            variables: {
                offset: lastOffset,
            },
        });
    };

    const handleFilterChange = useCallback(
        debounce(({ freeText, active }: UserListFilter) => {
            setFilter({
                freeText: freeText,
                active: active,
            });
            refetch({
                filters: { freeText: freeText.toLocaleLowerCase(), active: active },
                offset: 0,
            });
            setLastOffset(0);
        }, 800),
        [],
    );

    const handleClinicChange = (clinicId: string | null) => {
        refetch({
            clinicId: clinicId,
            offset: 0,
        });
        setLastOffset(0);
    };

    const refetchAfterMutation = () => {
        refetch({
            filters: { freeText: filter.freeText.toLocaleLowerCase(), active: filter.active },
            offset: 0,
        });
    };

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };
    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleImpersonateUserLogin = (user: User) => {
        setUser(user);
        setOrganizationsDialogOpen(true);
    };

    const handleClose = () => {
        setOrganizationsDialogOpen(false);
        setUser({} as User);
    };

    const handleConfirm = async (organizationId: string, wlLabId: string | undefined) => {
        if (wlLabId) {
            setWlLabId(wlLabId);
        }
        await getImpersonateUserToken({ variables: { userId: user._id, organizationId } });
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('common:DENTISTS')} />
            <Box mb={1}>
                <AdminDentistPageActions
                    onFilterChange={handleFilterChange}
                    onClinicChange={handleClinicChange}
                    onAddDentistDialogOpen={handleOpenDialog}
                />
            </Box>

            {dentistsLoading && networkStatus !== NetworkStatus.fetchMore ? (
                <Loader />
            ) : (
                <Fragment>
                    <DentistList
                        filteredDentists={dentists}
                        onFetchMore={handleFetchMore}
                        dentistsLoading={dentistsLoading}
                        onUserUpdated={refetchAfterMutation}
                        onLoginClick={handleImpersonateUserLogin}
                    />
                    <AddDentistDialog
                        dialogOpen={dialogOpen}
                        onDialogClose={handleCloseDialog}
                        onUserAdded={refetchAfterMutation}
                    />
                </Fragment>
            )}
            {organizationsDialogOpen ? (
                <ImpersonateUserOrganizationsDialog
                    open={organizationsDialogOpen}
                    linkedOrganizations={user.linkedOrganizations}
                    handleClose={handleClose}
                    handleConfirm={handleConfirm}
                />
            ) : undefined}
        </Fragment>
    );
};

export default AdminDentists;
